<script setup>
import { reset } from '@formkit/core';
import { ref, watch } from 'vue';

import dayjs from 'dayjs';

const props = defineProps({
  buttonText: {
    type: String,
    default: 'Create',
  },
  invoiceRequired: {
    type: String,
    default: 'required',
  },
  accounting: {
    type: Object || null,
    default: {},
  },
  units: {
    type: Array,
    default: [],
  },
  accountingCompanyOptions: {
    type: Array,
    default: [],
  },
});
const emit = defineEmits(['submitHandler']);
const initial = {
  name: null,
  description: null,
  expense_date: null,
  unit: 48,
  invoice: [],
  attachments: [],
  invoice_twd_amount: null,
  accounting_company: null,
};
const disabled = ref(false);
const disabledStatus = ['waiting', 'approve', 'paid'];
const canDeleteStatus = ['draft', 'waiting', 'incomplete'];

const formData = ref(JSON.parse(JSON.stringify(initial)));
const claim_date = ref(dayjs().format('YYYY-MM-DD'));
const errors = ref([]);
const invoiceImage = ref('');
const attachmentImageArray = ref([]);

const action = ref('waiting');
const isSubmitting = ref(false);

const actionOptions = ref([]);

const canDelete = ref(false);

const resetFormData = () => {
  reset('accountingForm');
  formData.value = JSON.parse(JSON.stringify(initial));
};

const handleSubmit = async (data, actionType) => {
  if (isSubmitting.value) return; // 防止重複提交
  isSubmitting.value = true;
  try {
    emit('submitHandler', data, actionType);
    // 不在這裡自動重置 isSubmitting，等待父組件調用 resetFormData
  } catch (error) {
    console.error('Submit error:', error);
    isSubmitting.value = false;
  }
};

const resetSubmitting = () => {
  isSubmitting.value = false;
};

defineExpose({
  resetFormData,
  resetSubmitting,
});

watch(
  () => props.accounting,
  value => {
    if (Object.keys(value).length) {
      disabled.value = disabledStatus.includes(value.status);
      actionOptions.value = ['submit', 'delete'];
      canDelete.value = canDeleteStatus.includes(value.status);
      formData.value.name = value.name;
      formData.value.unit = value.unit[0]['id'];
      formData.value.description = value.description;
      claim_date.value = dayjs(value.claim_date).format('YYYY-MM-DD');
      invoiceImage.value = value.invoice;
      attachmentImageArray.value = value.attachments;
      formData.value.invoice = [];
      formData.value.attachments = [];
      formData.value.invoice_twd_amount = value.invoice_twd_amount;
      formData.value.expense_date = dayjs(value.expense_date).format('YYYY-MM-DD');
      formData.value.accounting_company = value.company_id;
    } else {
      formData.value = JSON.parse(JSON.stringify(initial));
      claim_date.value = dayjs().format('YYYY-MM-DD');
      canDelete.value = false;
      actionOptions.value = [];
      attachmentImageArray.value = [];
      invoiceImage.value = '';
      disabled.value = false;
    }
  },
);
watch(
  () => props.accountingCompanyOptions,
  companies => {
    initial.accounting_company =
      companies.find(company => company.is_current)?.value || '';
  },
);
</script>

<template>
  <div class="my-2 text-right">
    <button
      v-if="canDelete"
      @click="$emit('submitHandler', formData, 'delete')"
      :disabled="isSubmitting"
      class="px-2 py-1 text-white rounded bg-rose-400 disabled:opacity-50"
    >
      cancel apply
    </button>
  </div>

  <FormKit
    type="form"
    :disabled="disabled || isSubmitting"
    id="accountingForm"
    v-model="formData"
    submit-label="confirm"
    :errors="errors"
    @submit="handleSubmit(formData, action)"
  >
    <FormKit
      v-if="props.accountingCompanyOptions.length > 1"
      type="select"
      label="accounting company"
      placeholder="Select a company"
      name="accounting_company"
      :options="props.accountingCompanyOptions"
      validation="required"
    />

    <div class="mb-2">
      <div class="text-sm font-bold">claim date</div>
      <div class="w-full px-2 py-2 mt-1 border border-gray-400 rounded disabled">
        {{ claim_date }}
      </div>
    </div>

    <FormKit
      type="select"
      label="unit"
      placeholder="Select a unit"
      name="unit"
      :options="props.units"
      validation="required"
    />

    <FormKit
      type="text"
      name="name"
      label="Item name"
      placeholder="e.g taxi"
      validation="required"
    />

    <FormKit
      type="textarea"
      name="description"
      label="Description"
      placeholder="e.g Japan"
      validation="required"
    />

    <FormKit
      type="date"
      :value="new Date()"
      name="expense_date"
      label="Expense date"
      validation="required"
      validation-visibility="live"
    />

    <FormKit
      type="file"
      name="invoice"
      label="Invoice"
      accept="image/*"
      :help="
        invoiceImage
          ? 'already has invoice, click view to review or upload again'
          : 'click and then take picture of invoice to upload'
      "
      :validation="invoiceRequired"
      multiple
    />

    <div class="mb-2" v-if="invoiceImage">
      <a
        class="px-2 py-1 text-white bg-indigo-400 rounded"
        :href="invoiceImage"
        target="_blank"
        >file</a
      >
    </div>

    <FormKit
      type="file"
      name="attachments"
      label="Your attachments"
      help="except invoice document"
      multiple
    />
    <div class="flex mb-2" v-if="attachmentImageArray.length">
      <template v-for="(item, index) in attachmentImageArray" :key="index">
        <a
          class="px-2 py-1 ml-2 text-white bg-indigo-400 rounded first:ml-0"
          :href="item"
          target="_blank"
          >{{ `file ${index + 1}` }}
        </a>
      </template>
    </div>

    <FormKit
      type="number"
      :value="new Date()"
      name="invoice_twd_amount"
      label="Invoice amount (TWD)"
      validation="required"
      min="1"
      syep="1"
      validation-visibility="live"
    />

    <FormKit
      v-if="actionOptions.length"
      type="select"
      label="Action"
      help="Save/ Submit for approve / Delete"
      v-model="action"
      :options="actionOptions"
    />
  </FormKit>
</template>
